import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Globe, ShoppingBag } from 'lucide-react';
import { SettingsWorkspace } from '@reach/settings-workspace';
import { getStorePlatformLabel, isPlatformManagedStore, useActiveStore } from '../store';

function EmptyWorkspaceState() {
  const navigate = useNavigate();

  return (
    <div className="theme-page-bg flex min-h-screen flex-col items-center justify-center px-6 py-24 text-center">
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border border-zinc-100 bg-white shadow-sm">
        <Globe size={30} className="text-zinc-300" />
      </div>
      <h1 className="mb-2 text-2xl font-black tracking-tight text-zinc-950">Choose a store first</h1>
      <p className="mb-8 max-w-sm text-sm font-medium leading-6 text-zinc-500">
        The workspace loads the selected store’s appearance, assistant, analytics, and subscription tabs.
      </p>
      <button
        onClick={() => navigate('/stores')}
        className="theme-btn-primary rounded-xl px-6 py-3 text-sm font-bold shadow-sm transition-all active:scale-[0.98]"
      >
        View stores
      </button>
    </div>
  );
}

function PlatformManagedFallback() {
  const navigate = useNavigate();
  const activeStore = useActiveStore();
  const label = getStorePlatformLabel(activeStore);
  const Icon = activeStore?.platform === 'shopify' ? ShoppingBag : Globe;

  return (
    <div className="theme-page-bg mx-auto flex min-h-screen max-w-5xl flex-col px-10 py-10">
      <button
        onClick={() => navigate('/stores')}
        className="mb-8 inline-flex w-fit items-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2 text-sm font-bold text-zinc-600 shadow-sm transition-colors hover:bg-zinc-50"
      >
        <ArrowLeft size={16} />
        Stores
      </button>

      <div className="relative overflow-hidden rounded-[2rem] border border-zinc-200 bg-white p-10 shadow-sm">
        <div className="absolute right-[-3rem] top-[-3rem] h-48 w-48 rounded-full bg-sky-50 blur-2xl" />
        <div className="relative z-10 flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
          <div className="max-w-2xl">
            <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-zinc-100 bg-zinc-50">
              <Icon size={24} className="text-zinc-700" />
            </div>
            <p className="mb-3 text-xs font-black uppercase tracking-[0.24em] text-sky-700">{label} fallback deferred</p>
            <h1 className="mb-4 text-3xl font-black tracking-tight text-zinc-950">{activeStore?.externalDomain}</h1>
            <p className="text-sm font-medium leading-7 text-zinc-500">
              This store is managed through its platform admin for now. We can still show subscription status in the console,
              but the embedded dashboard workspace is reserved for standard sites until Shopify and WordPress console auth are wired safely.
            </p>
          </div>

          <div className="grid min-w-[240px] gap-3">
            <button
              onClick={() => navigate('/stores')}
              className="theme-btn-primary rounded-xl px-5 py-3 text-sm font-bold shadow-sm"
            >
              Switch store
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MerchantWorkspace() {
  const [searchParams] = useSearchParams();
  const activeStore = useActiveStore();
  const initialTabId = searchParams.get('tab') === 'plans' ? 'plans' : undefined;

  if (!activeStore) return <EmptyWorkspaceState />;
  if (isPlatformManagedStore(activeStore)) return <PlatformManagedFallback />;

  return (
    <div className="theme-page-bg min-h-screen px-6 py-6 lg:px-8">
      <SettingsWorkspace
        shopDomain={activeStore.externalDomain}
        initialTabId={initialTabId}
        layoutVariant="headless"
        showOnboarding
      />
    </div>
  );
}
